import React, { ReactNode, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { SxProps } from '@mui/material'

import { useTranslation } from 'language/translator'
import { NAVBAR_COLLAPSED_TOOLTIP_OFFSET } from '../Navbar/lib'
import { logGlobalFilterActivated } from './events'

/* Atoms */
import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Collapse from 'components_new/atoms/Collapse'
import Divider from 'components_new/atoms/Divider'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import ListItem from 'components_new/atoms/List/ListItem'
import ListItemIcon from 'components_new/atoms/List/ListItemIcon'
import ListItemText from 'components_new/atoms/List/ListItemText'
import Skeleton from 'components_new/atoms/Skeleton'
import Tooltip from 'components_new/atoms/Tooltip'

/* Molecules */
import FilterPopover from 'components_new/molecules/FilterPopover'
import RadioItem from 'components_new/molecules/RadioItem'
import SearchAndSelect from 'components_new/molecules/SearchAndSelect'

/* Organisms */
import AdminList from '../AdminList'
import MyOrgUnitsDialog from './dialogs/MyOrgUnitsDialog'

/* Actions */
import { useCurrencyConverterActions } from 'redux/actions/CurrencyConverter'
import { useGlobalFilterActions } from 'redux/actions/GlobalFilter'

/* Hooks */
import { useCurrencyConverter } from 'redux/hooks/CurrencyConverter'
import { useGlobalFilter } from 'redux/hooks/GlobalFilter'
import { useMyScopes } from 'redux/hooks/MyScopes'
import {
  useAvailableUnits,
  useAvailableUnitsFetched
} from 'redux/hooks/AvailableUnits'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import {
  useIsManagementCompany,
  useAllowInternalFollowUp
} from 'redux/hooks/Customer'
import { useCompanyGroups } from 'redux/hooks/CompanyGroups'

/* Types */
import { ECurrency } from 'types/Enums'
import { GlobalFilterVariant } from 'types/GlobalGlobalFilter'

/* Utils */
import { CURRENCIES, CURRENCY_FLAGS } from '../CurrencyConverter/utils'

interface GlobalFilterProps {
  collapsed?: boolean
  showCurrencyConverter?: boolean
  sx?: SxProps
}

const GlobalFilter = React.forwardRef((props: GlobalFilterProps, ref) => {
  const { collapsed, showCurrencyConverter, sx } = props
  const { lang, t } = useTranslation()

  const [dialogOpen, setDialogOpen] = useState(false)

  // Get data from Redux (loaded on login)
  const myStandard = useMyScopes()
  const availableUnits = useAvailableUnits()
  const availableUnitsFetched = useAvailableUnitsFetched()
  const { account, isCompanyBased, isCompanyGroup } = useAccountSettings()
  const history = useHistory()
  const location = useLocation()

  const [companyGroups, loadingCompanyGroups] = useCompanyGroups()
  const isManagementCompany = useIsManagementCompany()
  const allowInternalFollowUp = useAllowInternalFollowUp()

  const currency = useCurrencyConverter()
  const { setCurrency } = useCurrencyConverterActions()

  const { updateGlobalFilter } = useGlobalFilterActions()

  // Get current filter from Redux
  const globalFilter = useGlobalFilter()
  const savedVariant = globalFilter.variant
  const savedCustomSelected = globalFilter.customSelected

  // Local temporary state (for editing before save)
  const [selected, setSelected] = useState<GlobalFilterVariant>(savedVariant)
  const [customSelected, setCustomSelected] =
    useState<string[]>(savedCustomSelected)
  const [selectedCompanyGroupId, setSelectedCompanyGroupId] = useState<
    string | null
  >(globalFilter.companyGroup?.id || null)

  // Sync local state when Redux state changes
  useEffect(() => {
    setSelected(savedVariant)
    setCustomSelected(savedCustomSelected)
    setSelectedCompanyGroupId(globalFilter.companyGroup?.id || null)
  }, [savedVariant, savedCustomSelected, globalFilter.companyGroup])

  // Sort available units by language
  const sortedOrgUnits = useMemo(() => {
    return [...availableUnits].sort((a, b) => a.localeCompare(b, lang))
  }, [availableUnits, lang])

  const handleSave = () => {
    let newSelected = selected

    // Set to all if no custom values are selected
    if (
      newSelected === GlobalFilterVariant.CUSTOM &&
      customSelected.length === 0
    ) {
      newSelected = GlobalFilterVariant.ALL
    }

    // Set to all if company group is selected but no company group is chosen
    if (
      newSelected === GlobalFilterVariant.COMPANY_GROUP &&
      !selectedCompanyGroupId
    ) {
      newSelected = GlobalFilterVariant.ALL
    }

    // Update Redux (also saves to localStorage)
    updateGlobalFilter(newSelected, customSelected, selectedCompanyGroupId)

    logGlobalFilterActivated(newSelected, history)

    handleClose(true)
  }

  const [controlledOpen, setControlledOpen] = useState<boolean>(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleClick = (event: any) => {
    // Reset temporary state to saved values when opening
    setSelected(savedVariant)
    setCustomSelected(savedCustomSelected)
    setSelectedCompanyGroupId(globalFilter.companyGroup?.id ?? null)
    setControlledOpen(true)
  }

  const handleClose = (fromSave = false) => {
    // Reset local state to saved Redux state on cancel
    setSelected(savedVariant)
    setCustomSelected(fromSave ? customSelected : savedCustomSelected)
    setSelectedCompanyGroupId(
      fromSave ? selectedCompanyGroupId : globalFilter.companyGroup?.id ?? null
    )
    setControlledOpen(false)
  }

  const handleChangeCustom = (value: string[]) => {
    setCustomSelected(value)
  }

  const handleOptionClick = (value: GlobalFilterVariant) => {
    setSelected(value)
  }

  const handleCompanyGroupChange = (value: string | null) => {
    setSelectedCompanyGroupId(value)
  }

  const [currencyOpen, setCurrencyOpen] = useState<boolean>(false)

  const handleCurrencyClick = () => {
    setCurrencyOpen(true)
  }

  const handleCurrencyClose = () => {
    setCurrencyOpen(false)
  }

  const allDataAccess = account?.organizational_unit_name === null
  const missingDataAccess =
    account?.organizational_unit_name === 'REMOVED_GROUP'
  // Get organizational unit name from account
  const dataAccess = missingDataAccess
    ? t('global', 'none')
    : account?.organizational_unit_name

  const invisibleOnCollapsedSx = {
    opacity: collapsed ? 0 : 1,
    transition: 'opacity 0.5s ease'
  }

  const noOverflowSx = {
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }

  const options: Record<
    GlobalFilterVariant,
    {
      onClick: () => void
      title: ReactNode | string
      value: GlobalFilterVariant
    }
  > = {
    [GlobalFilterVariant.ALL]: {
      onClick: () => {
        handleOptionClick(GlobalFilterVariant.ALL)
      },
      title: `${t('global', 'all')} ${t(
        'org_units',
        isCompanyBased ? 'units_company' : 'units',
        true
      )}`,
      value: GlobalFilterVariant.ALL
    },
    [GlobalFilterVariant.STANDARD]: {
      onClick: () => {
        handleOptionClick(GlobalFilterVariant.STANDARD)
      },
      title: (
        <>
          {t('org_units', isCompanyBased ? 'standard_company' : 'standard')}{' '}
          <b>({myStandard.length})</b>
        </>
      ),
      value: GlobalFilterVariant.STANDARD
    },
    [GlobalFilterVariant.CUSTOM]: {
      onClick: () => {
        handleOptionClick(GlobalFilterVariant.CUSTOM)
      },
      title: (
        <>
          {t('org_units', isCompanyBased ? 'custom_company' : 'custom')}{' '}
          <b>({savedCustomSelected.length})</b>
        </>
      ),
      value: GlobalFilterVariant.CUSTOM
    },
    [GlobalFilterVariant.COMPANY_GROUP]: {
      onClick: () => {
        handleOptionClick(GlobalFilterVariant.COMPANY_GROUP)
      },
      title: (
        <>
          {globalFilter.companyGroup
            ? globalFilter.companyGroup.name
            : t('global', 'preview_company_group')}
        </>
      ),
      value: GlobalFilterVariant.COMPANY_GROUP
    }
  }

  const loading = !globalFilter.isInitialized
  const notActive = location.pathname.includes('reports')

  const filteredOptions = Object.values(options).filter((option) => {
    if (option.value === GlobalFilterVariant.COMPANY_GROUP) {
      return isManagementCompany
    }

    return allowInternalFollowUp
  })

  if (filteredOptions.length === 0 || isCompanyGroup) {
    return null
  }

  const filterPopoverContent = (
    <>
      <Box sx={{ p: 1 }}>
        {filteredOptions.map((option) => {
          const isStandard = option.value === GlobalFilterVariant.STANDARD

          return (
            <Box key={option.value} sx={{ position: 'relative' }}>
              <RadioItem
                checked={option.value === selected}
                disabled={isStandard && myStandard.length === 0}
                fullWidth={true}
                hover={true}
                label={option.title}
                onChange={option.onClick}
                size="small"
              />
              {isStandard ? (
                <IconButton
                  onClick={(e) => {
                    setDialogOpen(true)
                    e.stopPropagation()
                  }}
                  size="small"
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)'
                  }}
                >
                  <Icon fontSize="small" name="Edit" />{' '}
                </IconButton>
              ) : null}
            </Box>
          )
        })}
      </Box>

      {/*-- custom --*/}
      <Collapse in={selected === GlobalFilterVariant.CUSTOM}>
        <Divider />
        <SearchAndSelect
          autoFocus={true}
          loading={!availableUnitsFetched}
          onChange={handleChangeCustom}
          options={sortedOrgUnits.map((property) => {
            return { id: property, title: property }
          })}
          size="small"
          sx={{ p: 2 }}
          value={customSelected}
        />
      </Collapse>

      {/*-- company group --*/}
      <Collapse in={selected === GlobalFilterVariant.COMPANY_GROUP}>
        <Divider />
        <SearchAndSelect
          autoFocus={true}
          loading={loadingCompanyGroups}
          multiple={false}
          nullable={allowInternalFollowUp}
          onChange={handleCompanyGroupChange}
          options={companyGroups.map((cg) => ({
            id: cg.id,
            title: cg.name
          }))}
          size="small"
          sx={{ p: 2 }}
          value={selectedCompanyGroupId}
        />
      </Collapse>

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          p: 1,
          pt: 0
        }}
      >
        <Button onClick={handleClose} variant="text">
          {t('global', 'cancel')}
        </Button>
        <Button onClick={handleSave}>{t('global', 'save')}</Button>
      </Box>
    </>
  )

  /*-- default --*/
  return (
    <>
      <Box ref={ref} sx={sx}>
        {allDataAccess ? null : (
          <Tooltip
            slotProps={NAVBAR_COLLAPSED_TOOLTIP_OFFSET}
            title={
              collapsed ? (
                <>
                  {t('global', 'data_access')}
                  <br />
                  <b>{dataAccess}</b>
                </>
              ) : null
            }
            placement={'right'}
          >
            <Box
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 0.5
              }}
            >
              <ListItem
                dense={true}
                sx={{
                  cursor: 'not-allowed',
                  px: 1,
                  bgcolor: missingDataAccess ? 'error.background' : undefined
                }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <Icon name="Access" sx={{ fontSize: '1.25rem' }} />
                </ListItemIcon>
                <ListItemText
                  primary={t('global', 'data_access')}
                  primaryTypographyProps={{ color: 'text.secondary' }}
                  secondary={loading ? <Skeleton variant="text" /> : dataAccess}
                  secondaryTypographyProps={{ variant: 'navigation' }}
                  sx={{
                    ...invisibleOnCollapsedSx,
                    ...noOverflowSx
                  }}
                />
                <Tooltip
                  title={
                    missingDataAccess ? (
                      <AdminList
                        header={{
                          title: t('data_access', 'missing_data_access_title')
                        }}
                        helperText={t(
                          'data_access',
                          'missing_data_access_body'
                        )}
                        size={'small'}
                      />
                    ) : (
                      t('data_access', 'tooltip')
                    )
                  }
                >
                  <Icon
                    name="Info"
                    sx={{
                      color: 'action.disabled',
                      cursor: 'default',
                      fontSize: '1.25rem',
                      ml: 1,
                      ...invisibleOnCollapsedSx,
                      ['&:hover']: {
                        color: 'inherit'
                      }
                    }}
                  />
                </Tooltip>
              </ListItem>
            </Box>
          </Tooltip>
        )}

        {missingDataAccess ? null : showCurrencyConverter ? (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden'
            }}
          >
            {/*-- org filter row --*/}
            <FilterPopover
              active={savedVariant !== GlobalFilterVariant.ALL}
              collapsed={collapsed}
              loading={loading}
              onClick={handleClick}
              onClose={handleClose}
              open={controlledOpen}
              popoverWidth={300}
              primary={t('org_units', 'global_filter')}
              secondary={options[savedVariant].title}
              sx={{ border: 'none', borderRadius: 0 }}
              tooltipSlotProps={NAVBAR_COLLAPSED_TOOLTIP_OFFSET}
              tooltipTitle={t('org_units', 'global_filter')}
              tooltipValue={options[savedVariant].title}
            >
              {filterPopoverContent}
            </FilterPopover>

            <Divider />

            {/*-- currency row --*/}
            <FilterPopover
              active={false}
              collapsed={collapsed}
              iconName="CurrencyExchange"
              onClick={handleCurrencyClick}
              onClose={handleCurrencyClose}
              open={currencyOpen}
              popoverWidth={160}
              secondary={
                <Box
                  component="span"
                  sx={{
                    alignItems: 'center',
                    display: 'inline-flex',
                    gap: 0.75
                  }}
                >
                  <span style={{ position: 'relative', top: '2px' }}>
                    {CURRENCY_FLAGS[currency ?? ECurrency.SEK]}
                  </span>
                  <span>{currency ?? ECurrency.SEK}</span>
                </Box>
              }
              size="medium"
              sx={{ border: 'none', borderRadius: 0 }}
              tooltipSlotProps={NAVBAR_COLLAPSED_TOOLTIP_OFFSET}
              tooltipTitle={`${CURRENCY_FLAGS[currency ?? ECurrency.SEK]} ${
                currency ?? ECurrency.SEK
              }`}
              tooltipValue={`${CURRENCY_FLAGS[currency ?? ECurrency.SEK]} ${
                currency ?? ECurrency.SEK
              }`}
            >
              <Box sx={{ p: 1 }}>
                {CURRENCIES.map((c) => (
                  <RadioItem
                    checked={currency === c}
                    disableText={true}
                    fullWidth={true}
                    hover={true}
                    key={c}
                    label={
                      <Box
                        component="span"
                        sx={{
                          alignItems: 'center',
                          display: 'inline-flex',
                          gap: 0.75
                        }}
                      >
                        <span style={{ position: 'relative', top: '2px' }}>
                          {CURRENCY_FLAGS[c]}
                        </span>
                        <span>{c}</span>
                      </Box>
                    }
                    onChange={() => {
                      setCurrency(c)
                      handleCurrencyClose()
                    }}
                    size="small"
                  />
                ))}
              </Box>
            </FilterPopover>
          </Box>
        ) : (
          <FilterPopover
            active={savedVariant !== GlobalFilterVariant.ALL}
            collapsed={collapsed}
            disabled={notActive}
            loading={loading}
            onClick={handleClick}
            onClose={handleClose}
            open={controlledOpen}
            popoverWidth={300}
            primary={t('org_units', 'global_filter')}
            secondary={
              options[notActive ? GlobalFilterVariant.ALL : savedVariant].title
            }
            showActive={!notActive}
            tooltipSlotProps={NAVBAR_COLLAPSED_TOOLTIP_OFFSET}
            tooltipTitle={t('org_units', 'global_filter')}
            tooltipValue={options[savedVariant].title}
          >
            {filterPopoverContent}
          </FilterPopover>
        )}
      </Box>

      {/*-- dialog --*/}
      <MyOrgUnitsDialog
        onClose={() => setDialogOpen(false)}
        onSave={() => {
          if (selected === GlobalFilterVariant.STANDARD) {
            handleSave()
          }
        }}
        open={dialogOpen}
      />
    </>
  )
})

export default GlobalFilter
